import { useEffect } from "react";
import { useBoardGroups } from "@hooks/api";
import { Checkbox, Skeleton } from "@vibe/core";
import { DropdownChevronDown, Warning } from "@vibe/icons";
import type { WizardBoardTarget, WizardGroupTarget } from "./syncWizard.types";

const GROUP_OPTIONS: ReadonlyArray<{ id: string; label: string }> = [
  { id: "include-subitems", label: "Include Subitems" },
  { id: "include-updates", label: "Include Updates" },
];

const GROUP_SKELETON_COUNT = 5;
const GROUP_SKELETON_WIDTHS = [72, 96, 60, 84, 108];

const GroupPillSkeleton = ({ width }: { width: number }) => (
  <div
    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-solid border-[#e6e9ef] bg-white h-[30px]"
    aria-hidden
  >
    <Skeleton type="rectangle" width={12} height={12} />
    <Skeleton type="text" width={width} height={12} />
  </div>
);

export function SyncWizardGroupSelector({
  targetBoard,
  boardName,
  boardColor,
  targetGroup,
  setTargetGroup,
  processSubitems,
  processUpdates,
  setProcessSubitems,
  setProcessUpdates,
}: {
  targetBoard: WizardBoardTarget | null;
  boardName: string;
  boardColor: string;
  targetGroup: WizardGroupTarget | null;
  setTargetGroup: (targetGroup: WizardGroupTarget | null) => void;
  processSubitems: boolean;
  processUpdates: boolean;
  setProcessSubitems: (value: boolean) => void;
  setProcessUpdates: (value: boolean) => void;
}) {
  const { boardGroups, fetchGroupsForBoard, loadingBoardId, errorBoardId, mutateBoardGroups } =
    useBoardGroups();

  const targetBoardId = targetBoard?.id ?? null;

  useEffect(() => {
    if (targetBoardId) {
      fetchGroupsForBoard(targetBoardId);
    }
  }, [targetBoardId, fetchGroupsForBoard]);

  const groups = boardGroups[targetBoardId ?? ""] ?? [];
  const isLoadingGroups =
    !!targetBoardId && loadingBoardId === targetBoardId && groups.length === 0;
  const hasGroupsError = !!targetBoardId && errorBoardId === targetBoardId;

  const handleRetryGroups = () => {
    if (!targetBoardId) return;
    void mutateBoardGroups();
    fetchGroupsForBoard(targetBoardId);
  };

  const handleSelectGroup = (group: WizardGroupTarget) => {
    const nextGroup = targetGroup?.id === group.id ? null : group;
    setTargetGroup(nextGroup);
  };

  return (
    <div>
      <p className="text-[11px] font-semibold text-[#323338] !mb-2">
        Select group in <span style={{ color: boardColor }}>{boardName}</span>
      </p>

      <div className="flex flex-wrap gap-2">
        {hasGroupsError ? (
          <div className="w-full rounded-lg border border-solid border-[#e6e9ef] bg-[#fafbff] px-2.5 py-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 min-w-0">
                <Warning size={14} color="#676879" />
                <p className="text-[11px] text-[#676879] !mb-0 truncate">
                  Couldn&apos;t load groups.
                </p>
              </div>
              <button
                type="button"
                onClick={handleRetryGroups}
                className="border-0 bg-transparent p-0 text-[11px] text-[#0073ea] hover:underline cursor-pointer flex-shrink-0"
              >
                Retry
              </button>
            </div>
          </div>
        ) : isLoadingGroups
          ? Array.from({ length: GROUP_SKELETON_COUNT }).map((_, idx) => (
              <GroupPillSkeleton
                key={`group-skeleton-${idx}`}
                width={GROUP_SKELETON_WIDTHS[idx % GROUP_SKELETON_WIDTHS.length]}
              />
            ))
          : groups.map((group) => {
              const isSelected = targetGroup?.id === group.id;
              return (
                <div
                  role="button"
                  aria-pressed={isSelected}
                  key={group.id}
                  onClick={() =>
                    handleSelectGroup({
                      id: group.id,
                      name: group.title,
                      color: group.color ?? "#676879",
                    })
                  }
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-solid text-xs transition-all cursor-pointer ${
                    isSelected
                      ? "border-[#0073ea] bg-[#f0f7ff] text-[#0073ea] font-semibold"
                      : "border-[#e6e9ef] bg-white text-[#323338] hover:border-[#c3c6d4]"
                  }`}
                >
                  <div>
                    <div
                      className="w-3 h-3 rounded-sm"
                      style={{ backgroundColor: group.color }}
                    ></div>
                  </div>
                  {group.title}
                </div>
              );
            })}
      </div>

      <details
        className="group mt-3 border border-solid border-[#e6e9ef] rounded-lg overflow-hidden"
        open
      >
        <summary className="flex items-center justify-between px-3 py-2.5 cursor-pointer select-none bg-white hover:bg-[#fafafa] transition-colors list-none">
          <span className="text-sm text-[#323338]">Options</span>

          <span className="flex items-center transition-transform duration-200 group-open:rotate-180">
            <DropdownChevronDown size={16} color="#676879" />
          </span>
        </summary>
        <div className="flex flex-col gap-0 border-t border-solid border-x-0 border-b-0 border-[#e6e9ef]">
          {GROUP_OPTIONS.map(({ id, label }) => (
            <div
              key={id}
              className="flex items-center gap-2.5 px-3 py-2.5 cursor-pointer hover:bg-[#fafafa] transition-colors"
            >
              <Checkbox
                label={label}
                checked={id === "include-subitems" ? processSubitems : processUpdates}
                onChange={(e) => {
                  if (id === "include-subitems") {
                    setProcessSubitems(e.target.checked);
                  } else {
                    setProcessUpdates(e.target.checked);
                  }
                }}
              />
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}
